import { Component, OnInit, ViewChild } from '@angular/core';

import {
  Ticket,
  TicketPriority,
  TicketSearchRequest,
  TicketStatus,
  TicketUser,
  CustomerSummary
} from 'src/api/models';
import { CustomerService, TicketService } from 'src/api/services';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { AzureAuthService } from 'src/service/azure-auth.service';
import { ToastrService } from 'ngx-toastr';
import { SupportTicketEditorModalComponent } from 'src/app/components/support-ticket-editor-modal/support-ticket-editor-modal.component';

@Component({
  selector: 'app-support-search',
  templateUrl: './support-search.component.html',
  styleUrls: ['./support-search.component.css']
})
export class SupportSearchComponent implements OnInit {
  @ViewChild('ticketEditor', { static: false }) ticketEditor: SupportTicketEditorModalComponent;

  statusTypes: TicketStatus[] = [];
  priorityTypes: TicketPriority[] = [];
  allCustomers: CustomerSummary[] = [];
  users: TicketUser[] = [];

  tickets: Ticket[] = [];
  selectedTicket: Ticket | null = null;
  newNote = '';

  searchText = '';
  statusFilterId: number | null = null;
  customerFilterId: number | null = null;
  assignedToUserFilterId: number | null = null;
  raisedByUserFilterId: number | null = null;

  page = 1;
  pageSize = 25;
  totalItems = 0;
  maxPages = 6;

  loading = false;

  constructor(
    private app: ApplicationConfigService,
    public azureAuthService: AzureAuthService,
    public ticketService: TicketService,
    private customerService: CustomerService,
    private toasterService: ToastrService
  ) {
    this.app.customerChanged$.subscribe(() => {
      this.onCustomerContextChanged();
    });
  }

  ngOnInit(): void {
    if (this.app.customerId == null) {
      this.app.customerId = this.azureAuthService.userProfile.customerId;
    }

    this.customerFilterId = this.app.customerId;

    this.loadLookupData();
    this.doSearch(true);
  }

  private onCustomerContextChanged(): void {
    this.customerFilterId = this.app.customerId;
    this.assignedToUserFilterId = null;
    this.raisedByUserFilterId = null;
    this.loadAssignableUsers();
    this.doSearch(true);
  }

  private loadLookupData(): void {
    this.ticketService.TicketGetTicketStatuses().subscribe(statuses => {
      this.statusTypes = statuses || [];
    });

    this.ticketService.TicketGetTicketPriorities().subscribe(priorities => {
      this.priorityTypes = priorities || [];
    });

    this.customerService.CustomerGetAllCustomers(false).subscribe(customers => {
      this.allCustomers = (customers || []).sort((a, b) => (a.customerName ?? '').localeCompare(b.customerName ?? ''));
    });

    this.loadAssignableUsers();
  }

  private loadAssignableUsers(customerId?: number): void {
    const resolvedCustomerId = customerId ?? this.customerFilterId;
    if (!resolvedCustomerId) {
      this.users = [];
      return;
    }

    this.ticketService.TicketGetAssignableUsers(resolvedCustomerId).subscribe(users => {
      this.users = users || [];
      this.clearInvalidUserFilters();
    }, () => {
      this.users = [];
      this.clearInvalidUserFilters();
    });
  }

  private clearInvalidUserFilters(): void {
    if (this.assignedToUserFilterId != null && !this.users.find(u => u.userId === this.assignedToUserFilterId)) {
      this.assignedToUserFilterId = null;
    }

    if (this.raisedByUserFilterId != null && !this.users.find(u => u.userId === this.raisedByUserFilterId)) {
      this.raisedByUserFilterId = null;
    }
  }

  private buildSearchRequest(): TicketSearchRequest {
    const request: TicketSearchRequest = {
      page: this.page,
      pageSize: this.pageSize
    };

    const trimmedSearch = (this.searchText || '').trim();
    if (trimmedSearch.length > 0) {
      request.searchText = trimmedSearch;
    }

    if (this.statusFilterId != null) {
      request.statusId = this.statusFilterId;
    }

    if (this.azureAuthService.isAdmin()) {
      if (this.customerFilterId != null) {
        request.customerId = this.customerFilterId;
      }
    } else if (this.app.customerId != null) {
      request.customerId = this.app.customerId;
    }

    if (this.assignedToUserFilterId != null) {
      request.assignedToUserId = this.assignedToUserFilterId;
    }

    if (this.raisedByUserFilterId != null) {
      request.raisedByUserId = this.raisedByUserFilterId;
    }

    return request;
  }

  doSearch(resetToFirstPage = false): void {
    if (resetToFirstPage) {
      this.page = 1;
    }

    if (this.loading) {
      return;
    }

    this.loading = true;
    this.ticketService.TicketSearchTickets(this.buildSearchRequest()).subscribe(result => {
      this.tickets = result?.tickets || [];
      this.totalItems = result?.totalCount ?? this.tickets.length;
      this.page = result?.page ?? this.page;
      this.pageSize = result?.pageSize ?? this.pageSize;
      this.loading = false;
    }, error => {
      this.loading = false;
      this.tickets = [];
      this.totalItems = 0;
      this.toasterService.error('Failed to search tickets: ' + error.message);
    });
  }

  clearFilters(): void {
    this.searchText = '';
    this.statusFilterId = null;
    this.assignedToUserFilterId = null;
    this.raisedByUserFilterId = null;
    this.customerFilterId = this.app.customerId;
    this.loadAssignableUsers();
    this.doSearch(true);
  }

  onCustomerFilterChanged(): void {
    this.assignedToUserFilterId = null;
    this.raisedByUserFilterId = null;
    this.loadAssignableUsers();
    this.doSearch(true);
  }

  pageChange(event: any): void {
    this.page = event.page;
    this.doSearch(false);
  }

  setPageSize(size: number): void {
    this.pageSize = size;
    this.doSearch(true);
  }

  openEditTicket(ticket: Ticket): void {
    this.selectedTicket = this.cloneTicket(ticket);
    this.newNote = '';
    if (this.selectedTicket?.customerId) {
      this.loadAssignableUsers(this.selectedTicket.customerId);
    }
    this.ticketEditor?.open();
  }

  private cloneTicket(ticket: Ticket): Ticket {
    return JSON.parse(JSON.stringify(ticket)) as Ticket;
  }

  updateTicket(ticket?: Ticket): void {
    const target = ticket ?? this.selectedTicket;
    if (!target) {
      return;
    }

    const customerId = target.customerId ?? this.app.customerId;
    this.ticketService.TicketUpdateTicket({ customerId, updateTicket: target }).subscribe(updatedTicket => {
      this.toasterService.success('Ticket updated successfully');
      this.patchTicketInList(updatedTicket);
      this.selectedTicket = this.cloneTicket(updatedTicket);
      this.doSearch(false);
    }, error => {
      this.toasterService.error('Failed to update ticket: ' + error.message);
    });
  }

  addNote(): void {
    if (!this.selectedTicket || !this.newNote?.trim()) {
      return;
    }

    this.ticketService.TicketCreateTicketNote({
      ticketId: this.selectedTicket.id,
      note: this.newNote
    }).subscribe(note => {
      this.toasterService.success('Note added successfully');
      if (!this.selectedTicket.notes) {
        this.selectedTicket.notes = [];
      }
      this.selectedTicket.notes.push(note);
      this.newNote = '';
      this.doSearch(false);
    }, error => {
      this.toasterService.error('Failed to add note: ' + error.message);
    });
  }

  closeTicket(ticket: Ticket): void {
    this.ticketService.TicketUpdateTicketStatus({ ticketId: ticket.id, statusId: 5 }).subscribe(() => {
      this.toasterService.success('Ticket closed successfully');
      if (this.selectedTicket) {
        this.selectedTicket.status.id = 5;
      }
      this.doSearch(false);
    }, error => {
      this.toasterService.error('Failed to close ticket: ' + error.message);
    });
  }

  isTicketAcknowledged(ticket: Ticket): boolean {
    return !!ticket?.acknowledgedDate;
  }

  isTicketCompleted(ticket: Ticket): boolean {
    if (!ticket) {
      return false;
    }

    if (ticket.status?.id != null) {
      return ticket.status.id >= 4;
    }

    return !!ticket.resolvedDate || !!ticket.closedDate;
  }

  canAcknowledgeTicket(ticket: Ticket): boolean {
    if (!ticket || this.isTicketAcknowledged(ticket)) {
      return false;
    }

    if (this.azureAuthService.isAdmin()) {
      return true;
    }

    const currentUserId = this.azureAuthService.userProfile?.userId;
    if (currentUserId != null && ticket.assignedToUserId != null && currentUserId === ticket.assignedToUserId) {
      return true;
    }

    const currentName = this.azureAuthService.userProfile?.fullName;
    return !!currentName && !!ticket.assignedToUser && currentName === ticket.assignedToUser;
  }

  acknowledgeTicket(ticket: Ticket): void {
    if (!this.canAcknowledgeTicket(ticket)) {
      return;
    }

    this.ticketService.TicketAcknowledgeTicket(ticket.id).subscribe(updatedTicket => {
      this.toasterService.success('Ticket acknowledged successfully');
      this.patchTicketInList(updatedTicket);
      if (this.selectedTicket && this.selectedTicket.id === updatedTicket.id) {
        this.selectedTicket = this.cloneTicket(updatedTicket);
      }
      this.doSearch(false);
    }, error => {
      this.toasterService.error('Failed to acknowledge ticket: ' + error.message);
    });
  }

  acceptOwnership(ticket: Ticket): void {
    const draft = this.cloneTicket(ticket);
    draft.assignedToUserId = this.azureAuthService.userProfile?.userId;

    this.ticketService.TicketUpdateTicket({ customerId: draft.customerId ?? this.app.customerId, updateTicket: draft }).subscribe(updatedTicket => {
      this.toasterService.success('Ownership accepted successfully');
      this.patchTicketInList(updatedTicket);
      if (this.selectedTicket && this.selectedTicket.id === updatedTicket.id) {
        this.selectedTicket = this.cloneTicket(updatedTicket);
      }
      this.doSearch(false);
    }, error => {
      this.toasterService.error('Failed to accept ownership: ' + error.message);
    });
  }

  getFilteredUsersForSelectedTicket(): TicketUser[] {
    if (!this.users || !this.selectedTicket) {
      return this.users || [];
    }

    return this.users.filter(user =>
      user.customerName === this.selectedTicket.customerName ||
      (user.email && user.email.includes('@indicium-dynamics.com.au'))
    );
  }

  onSelectedTicketCustomerChange(customerId?: number | null): void {
    if (customerId) {
      this.loadAssignableUsers(customerId);
      return;
    }

    this.users = [];
  }

  getSlaMetricCardClass(value?: number | null): string {
    if (value == null || Number.isNaN(value)) {
      return 'ticket-sla-dashboard__item--na';
    }

    if (value < 25) {
      return 'ticket-sla-dashboard__item--danger';
    }

    if (value < 50) {
      return 'ticket-sla-dashboard__item--warning';
    }

    return 'ticket-sla-dashboard__item--default';
  }

  getSlaLevelCardClass(level?: string | null): string {
    const normalized = (level || '').toLowerCase();
    if (normalized.includes('gold')) {
      return 'ticket-sla-dashboard__item--gold';
    }
    if (normalized.includes('silver')) {
      return 'ticket-sla-dashboard__item--silver';
    }
    if (normalized.includes('bronze')) {
      return 'ticket-sla-dashboard__item--bronze';
    }
    return 'ticket-sla-dashboard__item--na';
  }

  private getSlaTotalHours(ticket: Ticket, metric: 'acknowledge' | 'complete'): number | null {
    if (metric === 'acknowledge') {
      return ticket.acknowledgeSlaTotalHours ?? null;
    }

    return ticket.completeSlaTotalHours ?? ticket.resolveSlaThresholdHours ?? null;
  }

  private getSlaRemainingHours(ticket: Ticket, metric: 'acknowledge' | 'complete'): number | null {
    const source = ticket as Ticket & {
      acknowledgeSlaRemainingHours?: number;
      completeSlaRemainingHours?: number;
      AcknowledgeSlaRemainingHours?: number;
      CompleteSlaRemainingHours?: number;
    };

    const explicitRemaining = metric === 'acknowledge'
      ? (source.acknowledgeSlaRemainingHours ?? source.AcknowledgeSlaRemainingHours)
      : (source.completeSlaRemainingHours ?? source.CompleteSlaRemainingHours);

    if (explicitRemaining != null && !Number.isNaN(explicitRemaining)) {
      return explicitRemaining;
    }

    const percent = metric === 'acknowledge'
      ? ticket.acknowledgeSlaRemainingPercent
      : ticket.completeSlaRemainingPercent;
    const total = this.getSlaTotalHours(ticket, metric);
    if (percent == null || total == null || Number.isNaN(percent) || Number.isNaN(total)) {
      return null;
    }

    return (Math.max(0, Math.min(100, percent)) / 100) * total;
  }

  getSlaHoursDisplay(ticket: Ticket, metric: 'acknowledge' | 'complete'): string {
    const remaining = this.getSlaRemainingHours(ticket, metric);
    const total = this.getSlaTotalHours(ticket, metric);

    if (remaining == null || total == null) {
      return 'N/A';
    }

    return `${remaining.toFixed(1)}h / ${total.toFixed(1)}h`;
  }

  private patchTicketInList(updatedTicket: Ticket): void {
    const index = this.tickets.findIndex(t => t.id === updatedTicket.id);
    if (index !== -1) {
      this.tickets[index] = updatedTicket;
    }
  }
}
